import React, { useState, useEffect } from 'react';
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Alert,
} from 'reactstrap';
import styled from 'styled-components';
import documentService from '../../services/document.service';

const TrashContainer = styled.div`
  padding: 0;
`;

const TrashHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 2px solid #dee2e6;
`;

const TrashRow = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 15px;
  border-bottom: 1px solid #f0f0f0;
  transition: background 0.2s;
  border-radius: 6px;
  margin-bottom: 4px;

  &:hover {
    background: #f8f9fa;
  }
`;

const ItemIcon = styled.div`
  font-size: 28px;
  flex: 0 0 44px;
`;

const ItemInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ItemName = styled.div`
  font-weight: 600;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ItemMeta = styled.div`
  font-size: 0.8rem;
  color: #999;
  margin-top: 2px;
`;

const ActionGroup = styled.div`
  flex: 0 0 auto;
  display: flex;
  gap: 8px;
  margin-left: 15px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #aaa;

  .icon {
    font-size: 72px;
    margin-bottom: 15px;
  }

  p {
    font-size: 1rem;
    margin: 0;
  }
`;

interface TrashItem {
  id: number;
  name: string;
  type: 'file' | 'folder';
  fileType?: string;
  size?: number;
  deletedAt?: string;
  creator?: { id: number; userName: string };
}

interface Props {
  onBack: () => void;
}

const DocumentTrash: React.FC<Props> = ({ onBack }) => {
  const [files, setFiles] = useState<TrashItem[]>([]);
  const [folders, setFolders] = useState<TrashItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirmModal, setConfirmModal] = useState(false);
  const [emptyConfirmModal, setEmptyConfirmModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<TrashItem | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'danger'; text: string } | null>(null);

  useEffect(() => {
    loadTrash();
  }, []);

  const loadTrash = async () => {
    setLoading(true);
    try {
      const result = await documentService.getTrash();
      if (result.success) {
        setFiles(result.data.files || []);
        setFolders(result.data.folders || []);
      }
    } catch (error) {
      console.error('Error loading trash:', error);
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type: 'success' | 'danger', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 3000);
  };

  const handleRestore = async (item: TrashItem) => {
    try {
      const result = item.type === 'file'
        ? await documentService.restoreFile(item.id)
        : await documentService.restoreFolder(item.id);

      if (result.success) {
        showMessage('success', `Đã khôi phục "${item.name}" thành công!`);
        loadTrash();
      }
    } catch (error: any) {
      showMessage('danger', error || 'Lỗi khi khôi phục');
    }
  };

  const handlePermanentDelete = async () => {
    if (!selectedItem) return;
    try {
      const result = selectedItem.type === 'file'
        ? await documentService.permanentDeleteFile(selectedItem.id)
        : await documentService.permanentDeleteFolder(selectedItem.id);

      if (result.success) {
        showMessage('success', `Đã xóa vĩnh viễn "${selectedItem.name}"`);
        setConfirmModal(false);
        setSelectedItem(null);
        loadTrash();
      }
    } catch (error: any) {
      showMessage('danger', error || 'Lỗi khi xóa vĩnh viễn');
    }
  };

  const handleEmptyTrash = async () => {
    try {
      const result = await documentService.emptyTrash();
      if (result.success) {
        showMessage('success', 'Đã dọn sạch thùng rác!');
        setEmptyConfirmModal(false);
        loadTrash();
      }
    } catch (error: any) {
      showMessage('danger', error || 'Lỗi khi dọn sạch thùng rác');
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (item: TrashItem): string => {
    if (item.type === 'folder') return '📁';
    const ext = item.fileType?.toLowerCase();
    const iconMap: Record<string, string> = {
      pdf: '📕', doc: '📘', docx: '📘', txt: '📝',
      xls: '📊', xlsx: '📊', ppt: '📙', pptx: '📙',
      zip: '🗜️', rar: '🗜️', jpg: '🖼️', jpeg: '🖼️',
      png: '🎨', gif: '🎞️', svg: '🎨', mp4: '🎬',
      avi: '🎬', mov: '🎬', mp3: '🎵', wav: '🎵',
    };
    return iconMap[ext || ''] || '📄';
  };

  const allItems: TrashItem[] = [
    ...folders.map(f => ({ ...f, type: 'folder' as const })),
    ...files.map(f => ({ ...f, type: 'file' as const })),
  ];

  const totalCount = allItems.length;

  return (
    <TrashContainer>
      <TrashHeader>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Button color="secondary" outline size="sm" onClick={onBack}>
            ← Quay lại
          </Button>
          <h5 style={{ margin: 0 }}>🗑️ Thùng rác {totalCount > 0 && `(${totalCount})`}</h5>
        </div>
        {totalCount > 0 && (
          <Button
            color="danger"
            outline
            size="sm"
            onClick={() => setEmptyConfirmModal(true)}
          >
            🧹 Dọn sạch thùng rác
          </Button>
        )}
      </TrashHeader>

      {message && (
        <Alert color={message.type} style={{ marginBottom: '15px' }}>
          {message.text}
        </Alert>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
          Đang tải...
        </div>
      ) : totalCount === 0 ? (
        <EmptyState>
          <div className="icon">🗑️</div>
          <p>Thùng rác trống</p>
        </EmptyState>
      ) : (
        <div>
          <div style={{ fontSize: '0.85rem', color: '#999', marginBottom: '12px' }}>
            Các file/thư mục bị xóa sẽ xuất hiện ở đây. Bạn có thể khôi phục hoặc xóa vĩnh viễn.
          </div>
          {allItems.map((item) => (
            <TrashRow key={`${item.type}-${item.id}`}>
              <ItemIcon>{getFileIcon(item)}</ItemIcon>
              <ItemInfo>
                <ItemName>{item.name}</ItemName>
                <ItemMeta>
                  {item.type === 'folder' ? 'Thư mục' : item.fileType?.toUpperCase()}
                  {item.type === 'file' && item.size ? ` • ${formatFileSize(item.size)}` : ''}
                  {item.deletedAt
                    ? ` • Đã xóa lúc ${new Date(item.deletedAt).toLocaleString('vi-VN')}`
                    : ''}
                </ItemMeta>
              </ItemInfo>
              <ActionGroup>
                <Button
                  size="sm"
                  color="success"
                  outline
                  onClick={() => handleRestore(item)}
                  title="Khôi phục"
                >
                  🔄 Khôi phục
                </Button>
                <Button
                  size="sm"
                  color="danger"
                  outline
                  onClick={() => {
                    setSelectedItem(item);
                    setConfirmModal(true);
                  }}
                  title="Xóa vĩnh viễn"
                >
                  ❌ Xóa vĩnh viễn
                </Button>
              </ActionGroup>
            </TrashRow>
          ))}
        </div>
      )}

      {/* Permanent delete confirm modal */}
      <Modal isOpen={confirmModal} toggle={() => setConfirmModal(false)}>
        <ModalHeader toggle={() => setConfirmModal(false)}>⚠️ Xóa vĩnh viễn</ModalHeader>
        <ModalBody>
          Bạn có chắc muốn xóa vĩnh viễn{' '}
          <strong>"{selectedItem?.name}"</strong>?
          <br />
          <span style={{ color: '#dc3545', fontSize: '0.9rem' }}>
            Hành động này không thể hoàn tác.
          </span>
        </ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={handlePermanentDelete}>
            Xóa vĩnh viễn
          </Button>
          <Button color="secondary" onClick={() => setConfirmModal(false)}>
            Hủy
          </Button>
        </ModalFooter>
      </Modal>

      {/* Empty trash confirm modal */}
      <Modal isOpen={emptyConfirmModal} toggle={() => setEmptyConfirmModal(false)}>
        <ModalHeader toggle={() => setEmptyConfirmModal(false)}>🧹 Dọn sạch thùng rác</ModalHeader>
        <ModalBody>
          Bạn có chắc muốn xóa vĩnh viễn{' '}
          <strong>tất cả {totalCount} mục</strong> trong thùng rác?
          <br />
          <span style={{ color: '#dc3545', fontSize: '0.9rem' }}>
            Hành động này không thể hoàn tác.
          </span>
        </ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={handleEmptyTrash}>
            Dọn sạch
          </Button>
          <Button color="secondary" onClick={() => setEmptyConfirmModal(false)}>
            Hủy
          </Button>
        </ModalFooter>
      </Modal>
    </TrashContainer>
  );
};

export default DocumentTrash;
